import mixpanel from 'mixpanel-browser'
import * as Sentry from '@sentry/vue'

import config from '@/config.js'
import store from '@/store/index.js'

const MIXPANEL_TOKEN = config.mixpanelToken
const VERSION = '2.45.0'

const account = store.access.getAccount()
const isUserLoggedIn = store.access.isLoggedIn()

const TrackingPlugin = {}

const PRODUCTION_ENV = 'useast1-prod01'

TrackingPlugin.install = function(Vue) {
  Vue.prototype.trackingIsEnabled = function() {
    return account?.shareAnalytics
  }

  Vue.prototype.$configureTracking = function() {
    if (this.trackingIsEnabled()) {
      this.$enableTracking(isUserLoggedIn)
    }
  }

  Vue.prototype.$enableTracking = function() {
    initMixpanel(account, isUserLoggedIn)
    initSentry(Vue, account)
  }

  Vue.prototype.$trackMixpanel = function(eventName, data) {
    mixpanel.init(MIXPANEL_TOKEN)
    mixpanel.track(eventName, data)
  }

  Vue.prototype.$track = function(eventName, data) {
    if (this.trackingIsEnabled()) {
      this.$trackMixpanel(eventName, data)
    }
  }

  Vue.prototype.$logError = function(error) {
    console.error(error)

    if (config.environment === PRODUCTION_ENV) {
      Sentry.captureException(error)
    }
  }
}

function initMixpanel(user, userIsLoggedIn) {
  if (!userIsLoggedIn) return
  if (!user) throw new Error('user account not found in Mixpanel setup')

  const {
    _id: userId,
    isAdmin,
    isOrganizationAdmin,
    isCoach,
    isSiteAdmin,
  } = user

  const superProperties = {
    accessSource: 'msOffice',
    version: VERSION,
    organizationId: user.organization?._id ?? user.organization ?? null,
    siteId: user.site?._id ?? user.site ?? null,
    siteName: user.site?.name ?? null,
    country: user?.site?.countryCodes?.[0] ?? null,
    organization: user.organization.name,
    organizationType: user.organization.type,
    organizationPlan: user.organization.plan,
    joinedAt: user.joinedAt || null,
    isAdmin,
    isOrganizationAdmin,
    jobTitle: user.position || null,
    isCoach,
    isSiteAdmin,
    isOnlyUser: !isAdmin && !isOrganizationAdmin && !isCoach && !isSiteAdmin,
  }

  mixpanel.init(MIXPANEL_TOKEN)
  mixpanel.identify(userId)
  mixpanel.people.set(superProperties)
  mixpanel.register(superProperties)
  mixpanel.track('session.Start', { status: 'success' })
}

function initSentry(Vue, user) {
  try {
    Sentry.init({
      Vue,
      dsn:
        'https://b76550deadd240438ea6ef62141ea9d6@o230902.ingest.sentry.io/4505608574599168',
      environment: config.environment,
      replaysSessionSampleRate:
        config.environment === PRODUCTION_ENV ? 0.1 : 0.2,
      replaysOnErrorSampleRate:
        config.environment === PRODUCTION_ENV ? 1.0 : 0.1,
      integrations: [
        new Sentry.Replay({
          maskAllText: true,
          blockAllMedia: true,
          networkCaptureBodies: true,
          networkDetailAllowUrls: [/^https:\/\/(.+)api\.reverscore\.net/],
          networkResponseHeaders: ['x-rever-logid', 'content-security-policy'],
        }),
      ],
    })

    Sentry.configureScope(scope => {
      scope.setUser(user)
    })
  } catch (err) {
    console.error('Error initializing Sentry', err)
  }
}

export default TrackingPlugin
